import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { useLocation } from 'react-router-dom';
import { formatReadableAmount } from '../../../../Common/FormatFunctions/FormatAmount';
import { RingLoader } from 'react-spinners';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, TableFooter } from "@mui/material";
import PdfPreview from '../../../../Common/PdfPreview_Default';

const apikey = process.env.REACT_APP_API;

const SaleBillCessReport = () => {
  const location = useLocation();
  const Company_Name = sessionStorage.getItem('Company_Name')
  const Company_GSTNO = sessionStorage.getItem('Company_GSTNO')
  const searchParams = new URLSearchParams(location.search);
  const fromDate = searchParams.get('fromDate');
  const toDate = searchParams.get('toDate');
  const company_Code = searchParams.get('Company_Code');
  const Year_Code = searchParams.get('Year_Code');
  const Customer_Code = searchParams.get('Customer_Code');
  const [pdfPreview, setPdfPreview] = useState(null);

  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [grandTotals, setGrandTotals] = useState({
    SubTotal: 0,
    Bill_Amount: 0,
    Market_Cess: 0,
    Dekrekh: 0,
  });

  const API_URL = `${apikey}/SaleBill_RegisterReportForCess`;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear());
    return `${day}/${month}/${year}`;
  };

  useEffect(() => {
    const fetchReportData = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axios.get(API_URL, {
          params: {
            fromDate: fromDate,
            toDate: toDate,
            Company_Code: company_Code,
            Year_Code: Year_Code,
            Customer_Code: Customer_Code
          },
        });
        setReportData(response.data);
      } catch (error) {
        console.error('Error fetching report:', error);
        setError('Error fetching report');
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [API_URL]);

  // Ac_code filter (unchanged) — memoized so it keeps a stable reference
  // when reportData/Customer_Code haven't actually changed
  const finalReportData = useMemo(() => (
    !Customer_Code
      ? reportData.filter(item => Number(item.Customer_Code) > 0)
      : reportData
  ), [reportData, Customer_Code]);

  // ===== MERGE DUPLICATE RECORDS =====
  // The API can return more than one row per bill (e.g. one row per line
  // item from a join). Bill No / Date / Customer / Bill Amount / Sub Total
  // are the same on every duplicate row, so they're kept as-is from the
  // first row. Market Cess and Dekrekh are per-line values that need to be
  // ADDED together to get the bill's real totals.
  const uniqueReportData = useMemo(() => {
    const merged = new Map();

    finalReportData.forEach(item => {
      const key = `${item.doc_no}_${formatDate(item.doc_date)}_${item.Customer_Code}`;
      const Market_Cess = Number(item.Market_Cess) || 0;
      const Dekrekh = Number(item.Dekrekh) || 0;

      if (!merged.has(key)) {
        // First row for this bill: keep it as the base record (Bill Amount
        // and Sub Total stay exactly as given), and start the running
        // totals for Market Cess / Dekrekh.
        merged.set(key, {
          ...item,
          Market_Cess,
          Dekrekh,
        });
      } else {
        // Subsequent row for the same bill: only add Market Cess / Dekrekh.
        const existing = merged.get(key);
        existing.Market_Cess += Market_Cess;
        existing.Dekrekh += Dekrekh;
      }
    });

    return Array.from(merged.values());
  }, [finalReportData]);

  useEffect(() => {
    if (uniqueReportData.length > 0) {
      const totals = uniqueReportData.reduce(
        (acc, item) => {
          const Bill_Amount = Number(item.Bill_Amount) || 0;
          const SubTotal = Number(item.SubTotal) || 0;
          const Market_Cess = Number(item.Market_Cess) || 0;
          const Dekrekh = Number(item.Dekrekh) || 0;

          acc.Bill_Amount += Bill_Amount;
          acc.SubTotal += SubTotal;
          acc.Market_Cess += Market_Cess;
          acc.Dekrekh += Dekrekh;

          return acc;
        },
        {
          Bill_Amount: 0,
          SubTotal: 0,
          Market_Cess: 0,
          Dekrekh: 0,
        }
      );

      setGrandTotals(prev => ({
        ...prev,
        Bill_Amount: totals.Bill_Amount,
        SubTotal: totals.SubTotal,
        Market_Cess: totals.Market_Cess,
        Dekrekh: totals.Dekrekh,
      }));
    } else {
      setGrandTotals({ SubTotal: 0, Bill_Amount: 0, Market_Cess: 0, Dekrekh: 0 });
    }
  }, [uniqueReportData]);

  // ===== GROUP BY BILL DATE (instead of Customer) =====
  const groupByDate = (data) => {
    const grouped = {};

    data.forEach(item => {
      const dateKey = formatDate(item.doc_date); // display key, e.g. "19/09/2026"
      const Bill_Amount = Number(item.Bill_Amount) || 0;
      const SubTotal = Number(item.SubTotal) || 0;
      const Market_Cess = Number(item.Market_Cess) || 0;
      const Dekrekh = Number(item.Dekrekh) || 0;

      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          rawDate: item.doc_date, // kept for correct chronological sorting
          items: [],
          Bill_Amount: 0,
          SubTotal: 0,
          Market_Cess: 0,
          Dekrekh: 0,
        };
      }

      grouped[dateKey].items.push(item);
      grouped[dateKey].Bill_Amount += Bill_Amount;
      grouped[dateKey].SubTotal += SubTotal;
      grouped[dateKey].Market_Cess += Market_Cess;
      grouped[dateKey].Dekrekh += Dekrekh;
    });

    return grouped;
  };

  const groupedData = groupByDate(uniqueReportData);

  // Sort date groups chronologically (object key order isn't guaranteed date order)
  const sortedDateKeys = Object.keys(groupedData).sort(
    (a, b) => new Date(groupedData[a].rawDate) - new Date(groupedData[b].rawDate)
  );

  let TOTAL_COLUMNS = 6; // Bill No | Customer Name | Bill Amount | Sub Total | Market Cess | DekRekh

  // ===== EXPORT TO EXCEL (grouped by date) =====
  const handleExportToExcel = () => {
    const wb = XLSX.utils.book_new();
    const headers = [
      "Bill No", "Bill Date", "Customer Name", "Bill Amount", "Sub Total", "Market Cess", "DekRekh"
    ];

    const groupedByDate = {};
    uniqueReportData.forEach(item => {
      const dateKey = formatDate(item.doc_date);
      if (!groupedByDate[dateKey]) {
        groupedByDate[dateKey] = { rawDate: item.doc_date, items: [] };
      }
      groupedByDate[dateKey].items.push(item);
    });

    const sortedKeys = Object.keys(groupedByDate).sort(
      (a, b) => new Date(groupedByDate[a].rawDate) - new Date(groupedByDate[b].rawDate)
    );

    const formattedData = [];
    let isFirstDate = true;

    sortedKeys.forEach(dateKey => {
      const dateRows = groupedByDate[dateKey].items;

      // Repeat header row for every date group after the first
      if (!isFirstDate) {
        formattedData.push({
          "Bill No": "Bill No",
          "Bill Date": "Bill Date",
          "Customer Name": "Customer Name",
          "Bill Amount": "Bill Amount",
          "Sub Total": "Sub Total",
          "Market Cess": "Market Cess",
          "DekRekh": "DekRekh",
        });
      }

      // Date group label row
      formattedData.push({
        "Bill No": "",
        "Bill Date": `Date: ${dateKey}`,
        "Customer Name": "",
        "Bill Amount": "",
        "Sub Total": "",
        "Market Cess": "",
        "DekRekh": "",
      });

      // Bill rows for this date
      dateRows.forEach(item => {
        formattedData.push({
          "Bill No": item.doc_no,
          "Bill Date": dateKey,
          "Customer Name": item.CustomerName,
          "Bill Amount": Number(item.Bill_Amount) || 0,
          "Sub Total": Number(item.SubTotal) || 0,
          "Market Cess": Number(item.Market_Cess) || 0,
          "DekRekh": Number(item.Dekrekh) || 0,
        });
      });

      // Subtotal row for this date
      const Bill_Amount = dateRows.reduce((sum, i) => sum + (Number(i.Bill_Amount) || 0), 0);
      const SubTotal = dateRows.reduce((sum, i) => sum + (Number(i.SubTotal) || 0), 0);
      const Market_Cess = dateRows.reduce((sum, i) => sum + (Number(i.Market_Cess) || 0), 0);
      const Dekrekh = dateRows.reduce((sum, i) => sum + (Number(i.Dekrekh) || 0), 0);

      formattedData.push({
        "Bill No": "",
        "Bill Date": "",
        "Customer Name": "Total",
        "Bill Amount": Bill_Amount,
        "Sub Total": SubTotal,
        "Market Cess": Market_Cess,
        "DekRekh": Dekrekh,
      });

      // Spacer row
      formattedData.push({
        "Bill No": "", "Bill Date": "", "Customer Name": "",
        "Bill Amount": "", "Sub Total": "", "Market Cess": "", "DekRekh": "",
      });

      isFirstDate = false;
    });

    const ws = XLSX.utils.json_to_sheet(formattedData, { header: headers });

    ws["!cols"] = [
      { wch: 12 }, { wch: 15 }, { wch: 25 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 12 },
    ];

    Object.keys(ws).forEach(cell => {
      if (cell.startsWith("!")) return;
      const value = ws[cell].v;
      if (
        (typeof value === "string" && value.startsWith("Date:")) ||
        (typeof value === "string" && value === "Total") ||
        headers.includes(value)
      ) {
        ws[cell].s = { font: { bold: true } };
      }
    });

    XLSX.utils.book_append_sheet(wb, ws, "CustomerCessReport");
    XLSX.writeFile(wb, "CustomerCessReport.xlsx");
  };

  const handlePrint = () => {
    const companyName = Company_Name;
    const doc = generatePDFDocument(companyName);
    const pdfUrl = URL.createObjectURL(doc);
    const printWindow = window.open(pdfUrl);
    printWindow.onload = function () {
      setTimeout(() => {
        printWindow.print();
      }, 500);
    };
  };

  // ===== PDF GENERATION (grouped by date) =====
  const generatePDFDocument = (companyName) => {
    const doc = new jsPDF();

    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text(companyName, doc.internal.pageSize.width / 2, 15, { align: 'center' });

    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`GSTN : ${Company_GSTNO}`, doc.internal.pageSize.width / 2, 23, { align: 'center' });

    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text("Customer Cess Report Print", doc.internal.pageSize.width / 2, 32, { align: 'center' });

    const startY = 42;

    // Group by date
    const groupedByDate = {};
    uniqueReportData.forEach(item => {
      const dateKey = formatDate(item.doc_date);
      if (!groupedByDate[dateKey]) {
        groupedByDate[dateKey] = { rawDate: item.doc_date, items: [] };
      }
      groupedByDate[dateKey].items.push(item);
    });

    const sortedKeys = Object.keys(groupedByDate).sort(
      (a, b) => new Date(groupedByDate[a].rawDate) - new Date(groupedByDate[b].rawDate)
    );

    let currentY = startY;
    const pageHeight = doc.internal.pageSize.height;

    sortedKeys.forEach(dateKey => {
      const group = groupedByDate[dateKey];

      // Start a new page if a date group header wouldn't fit
      if (currentY > pageHeight - 30) {
        doc.addPage();
        currentY = 15;
      }

      doc.setFontSize(8);
      doc.setFont(undefined, "bold");
      doc.text("Date:", 15, currentY);
      doc.setFont(undefined, "normal");
      doc.text(String(dateKey), 28, currentY);
      currentY += 3;

      const tableData = [
        ["Bill No", "Customer Name", "Bill Amount", "Sub Total", "Market Cess", "DekRekh"],
        ...group.items.map(item => [
          item.doc_no,
          item.CustomerName,
          formatReadableAmount(item.Bill_Amount),
          formatReadableAmount(item.SubTotal),
          formatReadableAmount(item.Market_Cess),
          formatReadableAmount(item.Dekrekh),
        ]),
        [
          { content: "Total", styles: { halign: 'right', fontStyle: 'bold' } },
          "",
          { content: formatReadableAmount(group.Bill_Amount), styles: { fontStyle: 'bold' } },
          { content: formatReadableAmount(group.SubTotal), styles: { fontStyle: 'bold' } },
          { content: formatReadableAmount(group.Market_Cess), styles: { fontStyle: 'bold' } },
          { content: formatReadableAmount(group.Dekrekh), styles: { fontStyle: 'bold' } },
        ]
      ];

      doc.autoTable({
        head: [tableData[0]],
        body: tableData.slice(1, -1),
        foot: [tableData[tableData.length - 1]],
        startY: currentY,
        styles: {
          fontSize: 6,
          cellPadding: { top: 0.5, bottom: 0.5, left: 1, right: 1 },
          halign: 'center',
        },
        headStyles: {
          fillColor: [255, 0, 0],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
        },
        footStyles: {
          fillColor: [255, 243, 205],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
        },
        columnStyles: {
          0: { cellWidth: 15, halign: 'left' },
          1: { cellWidth: 40, halign: 'left' },
          2: { cellWidth: 25, halign: 'right' },
          3: { cellWidth: 25, halign: 'right' },
          4: { cellWidth: 25, halign: 'right' },
          5: { cellWidth: 25, halign: 'right' },
        },
      });

      currentY = doc.lastAutoTable.finalY + 4;
    });

    // ===== Grand total block =====
    if (currentY > pageHeight - 20) {
      doc.addPage();
      currentY = 15;
    }

    const grandTotalRow = [
      { content: 'Grand Total', styles: { halign: 'right', fontStyle: 'bold' } },
      { content: formatReadableAmount(grandTotals.Bill_Amount), styles: { halign: 'right', fontStyle: 'bold' } },
      { content: formatReadableAmount(grandTotals.SubTotal), styles: { halign: 'right', fontStyle: 'bold' } },
      { content: formatReadableAmount(grandTotals.Market_Cess), styles: { halign: 'right', fontStyle: 'bold' } },
      { content: formatReadableAmount(grandTotals.Dekrekh), styles: { halign: 'right', fontStyle: 'bold' } },
    ];

    doc.autoTable({
      body: [grandTotalRow],
      startY: currentY,
      theme: 'grid',
      margin: { right: 72 },
      styles: { fontSize: 6 },
      bodyStyles: {
        fillColor: [255, 255, 0],
        textColor: [0, 0, 0],
        fontStyle: 'bold'
      },
      columnStyles: {
        0: { cellWidth: 55, halign: 'right' },
        1: { cellWidth: 25, halign: 'right' },
        2: { cellWidth: 25, halign: 'right' },
        3: { cellWidth: 25, halign: 'right' },
        4: { cellWidth: 25, halign: 'right' },
      }
    });

    const pdfBlob = doc.output('blob');
    setPdfPreview(URL.createObjectURL(pdfBlob));
    return pdfBlob;
  };

  return (
    <div style={{ marginTop: "10px" }}>
      <Typography variant="h6" style={{ textAlign: 'center', fontSize: "24px", fontWeight: "bold" }}>{Company_Name}</Typography>
      <Typography variant="h6" style={{ textAlign: 'center', fontSize: "16px", textDecoration: 'underline', fontWeight: "550" }}>GSTN : {Company_GSTNO}</Typography>
      <Typography variant="h6" style={{ textAlign: 'center', fontSize: "20px", fontWeight: "bold" }}>Customer Cess Report</Typography>
      {Customer_Code && reportData.length > 0 && (
        <>
          <Typography
            variant="subtitle1"
            style={{ textAlign: 'center', fontSize: "16px", marginBottom: "10px", fontWeight: "bold" }}
          >
            Customer Name: {reportData[0]?.CustomerName || ""}
          </Typography>
          <Typography
            variant="subtitle2"
            style={{ textAlign: 'center', fontSize: "14px", marginBottom: "10px", fontWeight: "normal" }}
          >
            Mobile No: {reportData[0]?.customermobile || ""}
          </Typography>
        </>
      )}

      <div className="mb-3 row align-items-center">
        <div className="col-auto">
          <button className="btn btn-secondary me-2" onClick={handlePrint}>
            Print
          </button>
          <button className="btn btn-success" onClick={handleExportToExcel}>
            Export to Excel
          </button>
          <button className="btn btn-secondary me-2" onClick={() => generatePDFDocument(Company_Name)}>
            Preview PDF
          </button>
          {pdfPreview && (
            <PdfPreview
              pdfData={pdfPreview}
              apiData={reportData[0]}
              label={"SaleRegister"}
            />
          )}
        </div>
      </div>

      <div style={{
        maxHeight: 'calc(100vh - 200px)',
        overflow: 'auto',
        position: 'relative',
        border: '1px solid #e0e0e0',
        marginBottom: '60px'
      }} id="reportTable">
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backgroundColor: 'white'
          }}>
            <TableRow>
              <TableCell style={{ textAlign: "left", fontWeight: "bold", backgroundColor: 'white' }}>Bill No</TableCell>
              {Customer_Code && (
                <TableCell style={{ textAlign: "center", fontWeight: "bold", backgroundColor: 'white' }}>Bill Date</TableCell>
              )}
              {!Customer_Code && (
                <TableCell style={{ textAlign: "center", fontWeight: "bold", backgroundColor: 'white' }}>Customer Name</TableCell>
              )}
              <TableCell style={{ textAlign: "right", fontWeight: "bold", backgroundColor: 'white' }}>Bill Amount</TableCell>
              <TableCell style={{ textAlign: "right", fontWeight: "bold", backgroundColor: 'white' }}>Sub Total</TableCell>
              <TableCell style={{ textAlign: "right", fontWeight: "bold", backgroundColor: 'white' }}>Market Cess</TableCell>
              <TableCell style={{ textAlign: "right", fontWeight: "bold", backgroundColor: 'white' }}>DekRekh</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {/* ===== GROUPED BY DATE (when no single Customer_Code is selected) ===== */}
            {!Customer_Code && sortedDateKeys.map((dateKey, gIdx) => {
              const group = groupedData[dateKey];
              return (
                <React.Fragment key={gIdx}>
                  {/* Date Header */}
                  <TableRow sx={{ backgroundColor: "#e3f2fd" }}>
                    <TableCell colSpan={TOTAL_COLUMNS} style={{ textAlign: "left" }}>
                      <span style={{ fontWeight: "bold" }}>Date:</span>{" "}
                      <span style={{ fontWeight: "normal", fontSize: "16px" }}>{dateKey}</span>
                    </TableCell>
                  </TableRow>

                  {/* Bills for this date */}
                  {group.items.map((item, idx) => (
                    <TableRow key={idx} sx={{ "&:hover": { backgroundColor: "#fdfd96" } }}>
                      <TableCell>{item.doc_no}</TableCell>
                      <TableCell style={{ textAlign: "center" }}>{item.CustomerName}</TableCell>
                      <TableCell style={{ textAlign: "right" }}>{formatReadableAmount(item.Bill_Amount)}</TableCell>
                      <TableCell style={{ textAlign: "right" }}>{formatReadableAmount(item.SubTotal)}</TableCell>
                      <TableCell style={{ textAlign: "right" }}>{formatReadableAmount(item.Market_Cess)}</TableCell>
                      <TableCell style={{ textAlign: "right" }}>{formatReadableAmount(item.Dekrekh)}</TableCell>
                    </TableRow>
                  ))}

                  {/* Date Subtotal */}
                  <TableRow sx={{ backgroundColor: "#fff3cd", fontWeight: "bold" }}>
                    <TableCell colSpan={2} align="right">Total :</TableCell>
                    <TableCell align="right">{formatReadableAmount(group.Bill_Amount)}</TableCell>
                    <TableCell align="right">{formatReadableAmount(group.SubTotal)}</TableCell>
                    <TableCell align="right">{formatReadableAmount(group.Market_Cess)}</TableCell>
                    <TableCell align="right">{formatReadableAmount(group.Dekrekh)}</TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}

            {/* ===== SINGLE CUSTOMER (Customer_Code passed) → flat list ===== */}
            {Customer_Code && uniqueReportData.map((item, idx) => (
              <TableRow key={idx}>
                <TableCell>{item.doc_no}</TableCell>
                <TableCell style={{ textAlign: "center" }}>{formatDate(item.doc_date)}</TableCell>
                <TableCell style={{ textAlign: "right" }}>{formatReadableAmount(item.Bill_Amount)}</TableCell>
                <TableCell style={{ textAlign: "right" }}>{formatReadableAmount(item.SubTotal)}</TableCell>
                <TableCell style={{ textAlign: "right" }}>{formatReadableAmount(item.Market_Cess)}</TableCell>
                <TableCell style={{ textAlign: "right" }}>{formatReadableAmount(item.Dekrekh)}</TableCell>
              </TableRow>
            ))}

            {/* ===== Grand Total ===== */}
            <TableRow sx={{ backgroundColor: '#ffff99', fontWeight: 'bold' }}>
              <TableCell colSpan={2} style={{ textAlign: 'right' }}>Total:</TableCell>
              <TableCell style={{ textAlign: 'right' }}>{formatReadableAmount(grandTotals.Bill_Amount)}</TableCell>
              <TableCell style={{ textAlign: 'right' }}>{formatReadableAmount(grandTotals.SubTotal)}</TableCell>
              <TableCell style={{ textAlign: 'right' }}>{formatReadableAmount(grandTotals.Market_Cess)}</TableCell>
              <TableCell style={{ textAlign: 'right' }}>{formatReadableAmount(grandTotals.Dekrekh)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>

      {loading && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 9999
        }}>
          <RingLoader size={80} />
        </div>
      )}
      {error && <div className="alert alert-danger">{error}</div>}
    </div>
  );
};

export default SaleBillCessReport;




// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import 'bootstrap/dist/css/bootstrap.min.css';
// import * as XLSX from 'xlsx';
// import { jsPDF } from 'jspdf';
// import 'jspdf-autotable';
// import { useLocation } from 'react-router-dom';
// import { formatReadableAmount } from '../../../../Common/FormatFunctions/FormatAmount';
// import { RingLoader } from 'react-spinners';
// import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, TableFooter } from "@mui/material";
// import PdfPreview from '../../../../Common/PdfPreview_Default';

// const apikey = process.env.REACT_APP_API;

// const SaleBillCessReport = () => {
//   const location = useLocation();
//   const Company_Name = sessionStorage.getItem('Company_Name')
//   const Company_GSTNO = sessionStorage.getItem('Company_GSTNO')
//   const searchParams = new URLSearchParams(location.search);
//   const fromDate = searchParams.get('fromDate');
//   const toDate = searchParams.get('toDate');
//   const company_Code = searchParams.get('Company_Code');
//   const Year_Code = searchParams.get('Year_Code');
//   const Customer_Code = searchParams.get('Customer_Code');
//   const [pdfPreview, setPdfPreview] = useState(null);

//   const [reportData, setReportData] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState('');

//   const [grandTotals, setGrandTotals] = useState({
//     SubTotal: 0,
//     Bill_Amount: 0,
//     Market_Cess: 0,
//     Dekrekh: 0,
//   });

//   const API_URL = `${apikey}/SaleBill_RegisterReportForCess`;

//   console.log("Calling API:", API_URL);


//   const formatDate = (dateString) => {
//     const date = new Date(dateString);
//     const day = String(date.getDate()).padStart(2, '0');
//     const month = String(date.getMonth() + 1).padStart(2, '0');
//     const year = String(date.getFullYear());
//     return `${day}/${month}/${year}`;
//   };

//   useEffect(() => {
//     const fetchReportData = async () => {
//       setLoading(true);
//       setError('');
//       try {
//         const response = await axios.get(API_URL, {
//           params: {
//             fromDate: fromDate,
//             toDate: toDate,
//             Company_Code: company_Code,
//             Year_Code: Year_Code,
//             Customer_Code: Customer_Code
//           },
//         });
//         setReportData(response.data);
//       } catch (error) {
//         console.error('Error fetching report:', error);
//         setError('Error fetching report');
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchReportData();
//   }, [API_URL]);

//   useEffect(() => {
//     if (finalReportData.length > 0) {
//       const totals = finalReportData.reduce(
//         (acc, item) => {
//           const Bill_Amount = Number(item.Bill_Amount) || 0;
//           const SubTotal = Number(item.SubTotal) || 0;
//           const Market_Cess = Number(item.Market_Cess) || 0;
//           const Dekrekh = Number(item.Dekrekh) || 0;

//           // Total Awak Qty
//           acc.Bill_Amount += Bill_Amount;
//           acc.SubTotal += SubTotal;
//           acc.Market_Cess += Market_Cess;
//           acc.Dekrekh += Dekrekh;

//           // Ready for sale qty (ONLY when Readyforsale === 'Y')
//           // if (item.Readyforsale === "Y") {
//           //   acc.ReadyQty += Bill_Amount;
//           // }

//           // if (
//           //   item.Readyforsale === "N" &&
//           //   item.RejectDate &&
//           //   item.AuctionMark === "N" &&
//           //   item.sold === "N" &&
//           //   Number(item.salebilldocno) === 0
//           // ) {
//           //   acc.RejectedQty += Bill_Amount;
//           // }


//           return acc;
//         },
//         {
//           Bill_Amount: 0,
//           // ReadyQty: 0,
//           // RejectedQty: 0,
//           SubTotal: 0,
//           Market_Cess: 0,
//           Dekrekh: 0,
//         }
//       );

//       setGrandTotals(prev => ({
//         ...prev,
//         Bill_Amount: totals.Bill_Amount,
//         // ReadyQty: totals.ReadyQty,
//         // RejectedQty: totals.RejectedQty,
//         SubTotal: totals.SubTotal,
//         Market_Cess: totals.Market_Cess,
//         Dekrekh: totals.Dekrekh,
//       }));
//     }
//   }, [reportData]);

//   // const handleExportToExcel = () => {
//   //     const wb = XLSX.utils.book_new();
//   //     const headers = [
//   //         "Awak No", "Date", "Customer Code", "Customer Name", "Quantity", "Item Name", "Godown Name", "Line Name", "Ready For Sale",
//   //     ];

//   //     const formattedData = reportData.map(item => ({
//   //         "Awak No": item.Doc_No,
//   //         "Date": item.Doc_Date,
//   //         "Customer Code": item.Customer_Code,
//   //         "Customer Name": item.CustomerName,
//   //         "Quantity": Number(item.Awak_Qty) || 0,
//   //         "Item Name": item.ItemName,
//   //         "Godown Name": item.System_Name_E,
//   //         "Line Name": item.LineName,
//   //         "Ready For Sale": item.Readyforsale,

//   //     }));
//   //     const ws = XLSX.utils.json_to_sheet(formattedData, { header: headers });
//   //     const wsCols = [
//   //         { wch: 12, alignment: { horizontal: "left" } },    // Awak No
//   //         { wch: 15, alignment: { horizontal: "center" } },  // Date
//   //         { wch: 12, alignment: { horizontal: "center" } },  // Farmer Code
//   //         { wch: 25, alignment: { horizontal: "right" } },    // Farmer Name
//   //         { wch: 25, alignment: { horizontal: "left" } },    // Item Name
//   //         { wch: 25, alignment: { horizontal: "left" } },    // Godown Name
//   //         { wch: 25, alignment: { horizontal: "left" } },    // Line Name
//   //         { wch: 12, alignment: { horizontal: "right" } },   // Quantity
//   //         { wch: 12, alignment: { horizontal: "right" } },   // Despatched
//   //         { wch: 12, alignment: { horizontal: "right" } },   // Balance
//   //         { wch: 15, alignment: { horizontal: "left" } },    // Auction Mark
//   //     ];


//   //     ws["!cols"] = wsCols;
//   //     XLSX.utils.book_append_sheet(wb, ws, 'CustomerReport');
//   //     XLSX.writeFile(wb, 'CustomerReport.xlsx');
//   // };


//   const handleExportToExcel = () => {
//     const wb = XLSX.utils.book_new();
//     const headers = [
//       "Bill No", "Bill Date","Customer Name", "Bill Amount", "Sub Total", "Market Cess", "DekRekh"
//     ];

//     const groupedData = reportData.reduce((acc, item) => {
//       if (!acc[item.Customer_Code]) acc[item.Customer_Code] = [];
//       acc[item.Customer_Code].push(item);
//       return acc;
//     }, {});

//     const formattedData = [];
//     let isFirstFarmer = true;

//     Object.keys(groupedData).forEach(Customer_Code => {
//       const custmoreRows = groupedData[Customer_Code];
//       const Customername = custmoreRows[0].CustomerName;

//       // Insert headers only for subsequent farmers
//       if (!isFirstFarmer) {
//         formattedData.push({
//           "Bill No": "Bill No",
//           "Bill Date": "Bill Date",
//           "Customer Name": "Customer Name",
//           "Bill Amount": "Bill Amount",
//           "Sub Total": "Sub Total",
//           "Market Cess": "Market Cess",
//           "DekRekh": "DekRekh",
//         });
//       }

//       // Insert data rows
//       custmoreRows.forEach((item, index) => {
//         formattedData.push({
//           "Bill No": item.doc_no,
//           "Bill Date": formatDate(item.doc_date),
//           "Customer Name": index === 0 ? Customername : "",
//           "Bill Amount": Number(item.Bill_Amount) || 0,
//           "SubTotal": Number(item.SubTotal) || 0,
//           "Market_Cess": Number(item.Market_Cess) || 0,
//           "Dekrekh": Number(item.Dekrekh) || 0,
//         });
//       });

//       // Subtotal row
//       const Bill_Amount = custmoreRows.reduce((sum, i) => sum + (Number(i.Bill_Amount) || 0), 0);
//       formattedData.push({
//         "Bill No": "",
//         "Bill Date": "",
//         "Customer Name": "",
//         "Bill Amount": Bill_Amount,
//         "Sub Total": "",
//         "Market Cess": "",
//         "DekRekh": "",
//       });

//       // Empty row for spacing
//       formattedData.push({
//         "Bill No": "",
//         "Bill Date": "",
//         "Customer Name": "",
//         "Bill Amount": "",
//         "Sub Total": "",
//         "Market Cess": "",
//         "DekRekh": "",
//       });

//       isFirstFarmer = false;
//     });

//     const ws = XLSX.utils.json_to_sheet(formattedData, { header: headers });

//     // Set column widths
//     ws["!cols"] = [
//       { wch: 12 }, { wch: 15 }, { wch: 12 }, { wch: 25 }, { wch: 25 }, { wch: 25 }, { wch: 12 },
//     ];

//     // Bold Farmer Name, headers, and Total row
//     Object.keys(ws).forEach(cell => {
//       if (cell.startsWith("!")) return;
//       const value = ws[cell].v;
//       if (
//         (typeof value === "string" && value === "Customer Name") ||
//         (typeof value === "string" && value === "Total") ||
//         headers.includes(value)
//       ) {
//         ws[cell].s = { font: { bold: true } };
//       }
//     });

//     XLSX.utils.book_append_sheet(wb, ws, "CustomerCessReport");
//     XLSX.writeFile(wb, "CustomerCessReport.xlsx");
//   };


//   const handlePrint = () => {
//     const companyName = Company_Name;
//     const fromDateParam = searchParams.get('fromDate');
//     const toDateParam = searchParams.get('toDate');

//     const doc = generatePDFDocument(companyName,);
//     const pdfUrl = URL.createObjectURL(doc);
//     const printWindow = window.open(pdfUrl);
//     printWindow.onload = function () {
//       setTimeout(() => {
//         printWindow.print();
//       }, 500);
//     };
//   };

//   // const generatePDFDocument = (companyName,) => {
//   //     const doc = new jsPDF();

//   //     // Add header information
//   //     doc.setFontSize(12);
//   //     doc.setFont(undefined, 'bold');
//   //     doc.text(companyName, doc.internal.pageSize.width / 2, 15, { align: 'center' });

//   //     doc.setFontSize(10);
//   //     doc.setFont(undefined, 'normal');
//   //     doc.text(`GSTN : ${Company_GSTNO}`, doc.internal.pageSize.width / 2, 23, { align: 'center' });

//   //     doc.setFontSize(12);
//   //     doc.setFont(undefined, 'bold');
//   //     doc.text("Customer Report Print", doc.internal.pageSize.width / 2, 32, { align: 'center' });

//   //     doc.setFontSize(10);
//   //     doc.setFont(undefined, 'normal');
//   //     // doc.text(`${FormaDateBalanceSheet(fromDate)} to ${FormaDateBalanceSheet(toDate)}`, doc.internal.pageSize.width / 2, 40, { align: 'center' });

//   //     const startY = 45;

//   //     // Prepare table data
//   //     const tableData = [
//   //         [
//   //             "Awak No", "Date", "Customer Code", "Customer Name", "Quantity", "Item Name", "Godown Name", "Line Name", "Ready For Sale",
//   //         ],
//   //         ...reportData.map(item => [
//   //             item.Doc_No,
//   //             item.Doc_Date,
//   //             item.Customer_Code,
//   //             item.CustomerName,
//   //             formatReadableAmount(item.Awak_Qty),
//   //             item.ItemName,
//   //             item.System_Name_E,
//   //             item.LineName,
//   //             item.Readyforsale,
//   //         ]),
//   //         [
//   //             '', '', '', '', '', 'Total',
//   //             formatReadableAmount(grandTotals.Awak_Qty),
//   //             '',
//   //         ]
//   //     ];

//   //     // Add table to PDF
//   //     doc.autoTable({
//   //         head: [tableData[0]],
//   //         body: tableData.slice(1, -1),
//   //         foot: [tableData[tableData.length - 1]],
//   //         startY: startY,
//   //         styles: {
//   //             fontSize: 6,
//   //             cellPadding: { top: 0.5, bottom: 0.5, left: 1, right: 1 },
//   //             halign: 'center',
//   //         },
//   //         headStyles: {
//   //             fillColor: [255, 0, 0],
//   //             textColor: [255, 255, 255],
//   //             fontStyle: 'bold',
//   //         },
//   //         footStyles: {
//   //             fillColor: [255, 255, 0],
//   //             textColor: [0, 0, 0],
//   //             fontStyle: 'bold',
//   //         },
//   //         columnStyles: {
//   //             0: { cellWidth: 12, halign: 'left' },    // Awak No
//   //             1: { halign: 'center' },  // Date
//   //             2: { cellWidth: 15, halign: 'center' },  // Farmer Code
//   //             3: { halign: 'left' },    // Farmer Name
//   //             4: { cellWidth: 15, halign: 'center' },    // Item Name
//   //             5: { cellWidth: 20, halign: 'center' },    // Godown Name
//   //             6: { cellWidth: 14, halign: 'center' },    // Line Name
//   //             7: { halign: 'center' },   // Quantity
//   //             8: { halign: 'center' },   // Despatched
//   //             9: { cellWidth: 12, halign: 'center' },   // Balance
//   //             10: { cellWidth: 20, halign: 'center' },  // Auction Mark
//   //         },
//   //         margin: { top: startY },
//   //     });

//   //     const pdfBlob = doc.output('blob');
//   //     const pdfUrl = URL.createObjectURL(pdfBlob);
//   //     setPdfPreview(pdfUrl);
//   //     return pdfBlob;
//   // };


//   const generatePDFDocument = (companyName) => {
//     const doc = new jsPDF();

//     // ===== Header =====
//     doc.setFontSize(12);
//     doc.setFont(undefined, 'bold');
//     doc.text(companyName, doc.internal.pageSize.width / 2, 15, { align: 'center' });

//     doc.setFontSize(10);
//     doc.setFont(undefined, 'normal');
//     doc.text(`GSTN : ${Company_GSTNO}`, doc.internal.pageSize.width / 2, 23, { align: 'center' });

//     doc.setFontSize(12);
//     doc.setFont(undefined, 'bold');
//     doc.text("Customer Cess Report Print", doc.internal.pageSize.width / 2, 32, { align: 'center' });

//     const startY = 45;

//     // ===== Group data by Ac_code =====
//     const groupedData = {};

//     finalReportData.forEach(item => {
//       const groupKey = [
//         Company_Name,
//         item.CustomerName || "",
//         item.Mobile_No || ""
//       ].join("|");

//       if (!groupedData[groupKey]) {
//         groupedData[groupKey] = {
//           companyName: Company_Name,
//           customerName: item.CustomerName || "",
//           mobile: item.Mobile_No || "",
//           items: []
//         };
//       }

//       groupedData[groupKey].items.push(item);
//     });

//     let currentY = startY;

//     // ===== Loop through each farmer group =====
//     Object.values(groupedData).forEach(group => {

//       doc.setFontSize(6);
//       doc.setFont(undefined, "bold");
//       doc.text("Company Name:", 15, currentY);
//       doc.setFont(undefined, "normal");
//       doc.text(String(group.companyName), 40, currentY);
//       currentY += 4;

//       doc.setFont(undefined, "bold");
//       doc.text("Customer Name:", 15, currentY);
//       doc.setFont(undefined, "normal");
//       doc.text(String(group.customerName), 40, currentY);
//       currentY += 4;

//       doc.setFont(undefined, "bold");
//       doc.text("Mobile No:", 15, currentY);
//       doc.setFont(undefined, "normal");
//       doc.text(String(group.mobile), 40, currentY);
//       currentY += 2;

//       const getReadyStatus = (value) => {
//         if (value === "Y") return "Confirm";
//         if (value === "N") return "Rejected";
//         return "";
//       };




//       // ---- Farmer rows (keep original table structure) ----
//       const tableData = [
//         [
//           "Bill No", "Bill Date", "Bill Amount", "Sub Total", "Market Cess", "DekRekh"
//         ],
//         ...group.items.map(item => [
//           item.doc_no,
//           formatDate(item.doc_date),
//           formatReadableAmount(item.Bill_Amount),
//           formatReadableAmount(item.SubTotal),
//           formatReadableAmount(item.Market_Cess),
//           formatReadableAmount(item.Dekrekh),
//         ])
//       ];

//       doc.autoTable({
//         head: [tableData[0]],
//         body: tableData.slice(1),
//         startY: currentY,
//         styles: {
//           fontSize: 6,
//           cellPadding: { top: 0.5, bottom: 0.5, left: 1, right: 1 },
//           halign: 'center',
//         },
//         headStyles: {
//           fillColor: [255, 0, 0],
//           textColor: [255, 255, 255],
//           fontStyle: 'bold',
//         },
//         columnStyles: {
//           0: { cellWidth: 10, halign: 'left' },
//           1: { cellWidth: 15, halign: 'center' },
//           2: { cellWidth: 25, halign: 'right' },
//           3: { cellWidth: 25, halign: 'right' },
//           4: { cellWidth: 25, halign: 'right' },
//           5: { cellWidth: 25, halign: 'right' },
//         },

//       });


//       currentY = doc.lastAutoTable.finalY + 4;
//     });

//     const grandTotalRow = [
//       // { content: '', colSpan: 0 }, // columns 0–4
//       // {
//       //   content: 'Total',
//       //   styles: {
//       //     halign: 'right',
//       //     fontStyle: 'bold'
//       //   }
//       // },
//       {
//         content: 'Total',
//         styles: {
//           halign: 'right',
//           fontStyle: 'bold'
//         }
//       },

//       {
//         content: formatReadableAmount(grandTotals.Bill_Amount),
//         styles: {
//           halign: 'right',
//           fontStyle: 'bold'
//         }
//       },

//       {
//         content: formatReadableAmount(grandTotals.SubTotal),
//         styles: {
//           halign: 'right',
//           fontStyle: 'bold'
//         }
//       },
//       {
//         content: formatReadableAmount(grandTotals.Market_Cess),
//         styles: {
//           halign: 'right',
//           fontStyle: 'bold'
//         }
//       },
//       {
//         content: formatReadableAmount(grandTotals.Dekrekh),
//         styles: {
//           halign: 'right',
//           fontStyle: 'bold'
//         }
//       },

//     ];


//     doc.autoTable({
//       body: [grandTotalRow],
//       startY: currentY,
//       theme: 'grid',
//       margin: {
//         right: 72   // 👈 X-axis position (increase = move right)
//       },
//       styles: {
//         fontSize: 6,
//       },
//       bodyStyles: {
//         fillColor: [255, 255, 0],
//         textColor: [0, 0, 0],
//         fontStyle: 'bold'
//       },
//       columnStyles: {
//         0: { cellWidth: 25, halign: 'right' }, // SAME Quantity column
//         1: { cellWidth: 25, halign: 'right' },
//         0: { cellWidth: 25, halign: 'right' }, // SAME Quantity column
//         1: { cellWidth: 25, halign: 'right' },
//       }
//     });


//     const pdfBlob = doc.output('blob');
//     setPdfPreview(URL.createObjectURL(pdfBlob));
//     return pdfBlob;
//   };


//   // Group data by Ac_Code
//   // const groupByAcCode = (data) => {
//   //     const grouped = {};
//   //     data.forEach(item => {
//   //         const acKey = item.oldCustomerCode;
//   //         if (!grouped[acKey]) {
//   //             grouped[acKey] = {
//   //                 Customername: item.OLDCustomerName,
//   //                 mobile: item.customermobile,
//   //                 // awakNo: item.Doc_No,
//   //                 items: [],
//   //                 totalQty: 0
//   //             };
//   //         }
//   //         grouped[acKey].items.push(item);
//   //         grouped[acKey].totalQty += Number(item.Awak_Qty) || 0;
//   //         grouped[acKey].totalQty += Number(item.Amount) || 0;
//   //     });
//   //     return grouped;
//   // };


//   const groupByAcCode = (data) => {
//     const grouped = {};

//     data.forEach(item => {
//       const acKey = item.Customer_Code;
//       const Bill_Amount = Number(item.Bill_Amount) || 0;
//       const SubTotal = Number(item.SubTotal) || 0;
//       const Market_Cess = Number(item.Market_Cess) || 0;
//       const Dekrekh = Number(item.Dekrekh) || 0;

//       if (!grouped[acKey]) {
//         grouped[acKey] = {
//           Customername: item.CustomerName,
//           mobile: item.customermobile,
//           items: [],
//           Bill_Amount: 0,
//           SubTotal: 0,
//           Market_Cess: 0,
//           Dekrekh: 0,
//         };
//       }

//       grouped[acKey].items.push(item);
//       grouped[acKey].Bill_Amount += Bill_Amount;
//       grouped[acKey].SubTotal += SubTotal;
//       grouped[acKey].Market_Cess += Market_Cess;
//       grouped[acKey].Dekrekh += Dekrekh;


//       // if (item.Readyforsale === "Y") {
//       //   grouped[acKey].readyQty += Bill_Amount;
//       // }


//       // if (
//       //   item.Readyforsale === "N" &&
//       //   item.RejectDate &&
//       //   item.AuctionMark === "N" &&
//       //   item.sold === "N" &&
//       //   Number(item.salebilldocno) === 0
//       // )
//       {
//         grouped[acKey].rejectedQty += Bill_Amount;
//       }
//     });

//     return grouped;
//   };




//   const finalReportData = !Customer_Code
//     ? reportData.filter(item => Number(item.Customer_Code) > 0)
//     : reportData;


//   const groupedData = groupByAcCode(finalReportData);



//   const groupReportData = (data) => {
//     const groupedData = {};
//     data.forEach((item) => {
//       const key = `${item.Id}`;
//       if (!groupedData[key]) {
//         groupedData[key] = {
//           items: [],
//           Bill_Amount: 0,
//           SubTotal: 0,
//           Market_Cess: 0,
//           Dekrekh: 0,
//         };
//       }
//       groupedData[key].items.push(item);
//       // groupedData[key].totalQty += parseFloat(item.Bill_Amount) || 0;
//     });
//     return groupedData;
//   };

//   const groupedReportData = groupReportData(reportData);

//   const safeAmount = (val) => {
//     const num = Number(val);
//     return isFinite(num) ? num : 0;
//   };

//   let TOTAL_COLUMNS = 6;



//   return (
//     <div style={{ marginTop: "10px" }}>
//       <Typography variant="h6" style={{ textAlign: 'center', fontSize: "24px", fontWeight: "bold" }}>{Company_Name}</Typography>
//       <Typography variant="h6" style={{ textAlign: 'center', fontSize: "16px", textDecoration: 'underline', fontWeight: "550" }}>GSTN : {Company_GSTNO}</Typography>
//       <Typography variant="h6" style={{ textAlign: 'center', fontSize: "20px", fontWeight: "bold" }}>Customer Cess Report</Typography>
//       {Customer_Code && reportData.length > 0 && (
//         <>
//           <Typography
//             variant="subtitle1"
//             style={{
//               textAlign: 'center',
//               fontSize: "16px",
//               marginBottom: "10px",
//               fontWeight: "bold",
//             }}
//           >
//             Customer Name: {reportData[0]?.CustomerName || ""}
//           </Typography>
//           <Typography
//             variant="subtitle2"
//             style={{
//               textAlign: 'center',
//               fontSize: "14px",
//               marginBottom: "10px",
//               fontWeight: "normal",
//             }}
//           >
//             Mobile No: {reportData[0]?.customermobile || ""}
//           </Typography>
//         </>
//       )}

//       {/* <Typography variant="h6" style={{ textAlign: 'center', fontSize: "16px" }}>{fromDate ? FormaDateBalanceSheet(fromDate) : ""} to {toDate ? FormaDateBalanceSheet(toDate) : ""}</Typography> */}

//       <div className="mb-3 row align-items-center">
//         <div className="col-auto">
//           <button className="btn btn-secondary me-2" onClick={handlePrint}>
//             Print
//           </button>
//           <button className="btn btn-success" onClick={handleExportToExcel}>
//             Export to Excel
//           </button>
//           <button className="btn btn-secondary me-2" onClick={() => generatePDFDocument(Company_Name,)}>
//             Preview PDF
//           </button>
//           {pdfPreview && (
//             <PdfPreview
//               pdfData={pdfPreview}
//               apiData={reportData[0]}
//               label={"SaleRegister"}
//             />
//           )}
//         </div>
//       </div>

//       <div style={{
//         maxHeight: 'calc(100vh - 200px)',
//         overflow: 'auto',
//         position: 'relative',
//         border: '1px solid #e0e0e0',
//         marginBottom: '60px'
//       }} id="reportTable">
//         <Table sx={{ minWidth: 650 }} aria-label="simple table">
//           <TableHead sx={{
//             position: 'sticky',
//             top: 0,
//             zIndex: 1,
//             backgroundColor: 'white'
//           }}>
//             <TableRow>
//               <TableCell style={{ textAlign: "left", fontWeight: "bold", backgroundColor: 'white' }}>Bill No</TableCell>
//               <TableCell style={{ textAlign: "center", fontWeight: "bold", backgroundColor: 'white' }}>Bill Date</TableCell>
//               {/* <TableCell style={{ textAlign: "right", fontWeight: "bold", backgroundColor: 'white' }}>Quantity</TableCell> */}
//               {!Customer_Code && (
//                 <>
//                   {/* <TableCell style={{ textAlign: "center", fontWeight: "bold" }}>
//                     Customer Code
//                   </TableCell> */}
//                   {/* <TableCell style={{ textAlign: "center", fontWeight: "bold" }}>
//                                         Customer Name
//                                     </TableCell> */}
//                 </>
//               )}
//               <TableCell style={{ textAlign: "right", fontWeight: "bold", backgroundColor: 'white' }}>Bill Amount</TableCell>
//               <TableCell style={{ textAlign: "right", fontWeight: "bold", backgroundColor: 'white' }}>Sub Total</TableCell>
//               <TableCell style={{ textAlign: "right", fontWeight: "bold", backgroundColor: 'white' }}>Market Cess</TableCell>
//               <TableCell style={{ textAlign: "right", fontWeight: "bold", backgroundColor: 'white' }}>DekRekh</TableCell>
//             </TableRow>
//           </TableHead>

//           <TableBody>
//             {/* List all items for this Ac_Code
//                         {Array.isArray(reportData) && reportData.map((item, idx) => (
//                             <TableRow
//                                 key={idx}
//                                 sx={{
//                                     cursor: "pointer",
//                                     "&:hover": { backgroundColor: "#fdfd96" },
//                                 }}
//                             >

//                                 <TableCell>{item.Doc_No}</TableCell>
//                                 <TableCell style={{ textAlign: "center" }}>{item.Doc_Date}</TableCell>
//                                 {!Customer_Code && (
//                                     <>
//                                         <TableCell style={{ textAlign: "center" }}>
//                                             {item.Customer_Code}
//                                         </TableCell>
//                                         <TableCell style={{ textAlign: "center" }}>
//                                             {item.CustomerName}
//                                         </TableCell>
//                                     </>
//                                 )}
//                                 <TableCell style={{ textAlign: "right" }}>{formatReadableAmount(item.Awak_Qty)}</TableCell>
//                                 <TableCell style={{ textAlign: "center" }}>{item.Item_Code}</TableCell>
//                                 <TableCell style={{ textAlign: "left" }}>{item.ItemName}</TableCell>
//                                 <TableCell style={{ textAlign: "center" }}>{item.Godown_code}</TableCell>
//                                 <TableCell style={{ textAlign: "left" }}>{item.System_Name_E}</TableCell>
//                                 <TableCell style={{ textAlign: "center" }}>{item.Line_code}</TableCell>
//                                 <TableCell style={{ textAlign: "left" }}>{item.LineName}</TableCell>
//                                 <TableCell style={{ textAlign: "center" }}>{item.Readyforsale}</TableCell>
//                             </TableRow>
//                         ))} */}


//             {!Customer_Code && Object.entries(groupedData).map(([acCode, group], gIdx) => (
//               <React.Fragment key={gIdx}>


//                 {/* Farmer Name Header */}
//                 <TableRow sx={{ backgroundColor: "#e3f2fd" }}>
//                   <TableCell colSpan={6} style={{ textAlign: "left" }}>
//                     <span style={{ fontWeight: "bold" }}>
//                       Customer Name:
//                     </span>{" "}
//                     <span style={{ fontWeight: "normal", fontSize: "16px" }}>
//                       {group.Customername}   (Account Code: {acCode})
//                     </span>
//                     <span style={{ fontWeight: "normal", fontSize: "16px", marginLeft: "20px" }}>
//                       Mobile No: {group.mobile}
//                     </span>
//                   </TableCell>

//                   {Array.from({ length: TOTAL_COLUMNS - 5 }).map((_, i) => (
//                     <TableCell key={i} />
//                   ))}
//                 </TableRow>



//                 {/* Farmer Rows */}
//                 {group.items.map((item, idx) => (
//                   <TableRow key={idx} sx={{ "&:hover": { backgroundColor: "#fdfd96" } }}>
//                     <TableCell>{item.doc_no}</TableCell>
//                     <TableCell style={{ textAlign: "center" }}>{formatDate(item.doc_date)}</TableCell>
//                     <TableCell style={{ textAlign: "right" }}>{formatReadableAmount(item.Bill_Amount)}</TableCell>
//                     <TableCell style={{ textAlign: "right" }}>{formatReadableAmount(item.SubTotal)}</TableCell>
//                     <TableCell style={{ textAlign: "right" }}>{formatReadableAmount(item.Market_Cess)}</TableCell>
//                     <TableCell style={{ textAlign: "right" }}>{formatReadableAmount(item.Dekrekh)}</TableCell>
//                   </TableRow>
//                 ))}

//                 {/* Farmer Total */}
//                 <TableRow sx={{ backgroundColor: "#fff3cd", fontWeight: "bold" }}>
//                   <TableCell colSpan={1} align="right">
//                     Total :
//                   </TableCell>

//                   <TableCell></TableCell>

//                   <TableCell align="right">
//                     {formatReadableAmount(group.Bill_Amount)}
//                   </TableCell>

//                   <TableCell align="right">
//                     {formatReadableAmount(group.SubTotal)}
//                   </TableCell>

//                   <TableCell align="right">
//                     {formatReadableAmount(group.Market_Cess)}
//                   </TableCell>

//                   <TableCell align="right">
//                     {formatReadableAmount(group.Dekrekh)}
//                   </TableCell>

//                   {Array.from({ length: TOTAL_COLUMNS - 5 }).map((_, i) => (
//                     <TableCell key={i} />
//                   ))}
//                 </TableRow>
//               </React.Fragment>
//             ))}


//             {/* WHEN Ac_code IS PASSED → NORMAL LIST */}
//             {Customer_Code && reportData.map((item, idx) => (
//               <TableRow key={idx}>
//                 <TableCell>{item.doc_no}</TableCell>
//                 <TableCell style={{ textAlign: "center" }}>{formatDate(item.doc_date)}</TableCell>
//                 <TableCell style={{ textAlign: "right" }}>{formatReadableAmount(item.Bill_Amount)}</TableCell>
//                 <TableCell style={{ textAlign: "right" }}>{formatReadableAmount(item.SubTotal)}</TableCell>
//                 <TableCell style={{ textAlign: "right" }}>{formatReadableAmount(item.Market_Cess)}</TableCell>
//                 <TableCell style={{ textAlign: "right" }}>{formatReadableAmount(item.Dekrekh)}</TableCell>
//               </TableRow>
//             ))}

//             <TableRow sx={{ backgroundColor: '#ffff99', fontWeight: 'bold' }}>
//               {/* Total label */}
//               <TableCell colSpan={1} style={{ textAlign: 'right' }}>
//                 Total:
//               </TableCell>

//               <TableCell></TableCell>


//               {/* Awak Qty */}
//               <TableCell style={{ textAlign: 'right' }}>
//                 {formatReadableAmount(grandTotals.Bill_Amount)}
//               </TableCell>

//               <TableCell style={{ textAlign: 'right' }}>
//                 {formatReadableAmount(grandTotals.SubTotal)}
//               </TableCell>

//               <TableCell style={{ textAlign: 'right' }}>
//                 {formatReadableAmount(grandTotals.Market_Cess)}
//               </TableCell>

//               <TableCell style={{ textAlign: 'right' }}>
//                 {formatReadableAmount(grandTotals.Dekrekh)}
//               </TableCell>

//               {/* Remaining empty cells */}
//               {Array.from({ length: TOTAL_COLUMNS - 5 }).map((_, i) => (
//                 <TableCell key={i} />
//               ))}
//             </TableRow>
//           </TableBody>
//         </Table>
//       </div>

//       {loading && (
//         <div style={{
//           position: 'fixed',
//           top: '50%',
//           left: '50%',
//           transform: 'translate(-50%, -50%)',
//           zIndex: 9999
//         }}>
//           <RingLoader size={80} />
//         </div>
//       )}
//       {error && <div className="alert alert-danger">{error}</div>}
//     </div>
//   );
// };

// export default SaleBillCessReport;
